import { Box, Stack, Typography } from '@mui/material';
import { compact } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';

import {
  AdminConfigurationsPage_TenantSsoConnectionFragment,
  AdminConfigurationsPageDocument,
  useCreateSsoConnectionFromAdminMutation,
  useCreateTenantSsoConnectionFromAdminMutation,
  useUpdateSsoConnectionFromAdminMutation,
  useUpdateTenantSsoConnectionFromAdminMutation,
} from '../graphql/AdminConfigurationsPage.generated';

type ExistingSSOConnection =
  AdminConfigurationsPage_TenantSsoConnectionFragment;

const SSO_REFETCH_CONFIG = {
  refetchQueries: [{ query: AdminConfigurationsPageDocument }],
  awaitRefetchQueries: true,
};

interface SSOSetupSectionProps {
  workosOrganizationId: string | null | undefined;
  existingSSOConnections: ExistingSSOConnection[];
}

interface SSOFormShape {
  displayName: string;
  emailDomains: { value: string }[];
  enableJIT: boolean;
}

function getDefaultValues(
  existingConnection: ExistingSSOConnection | undefined
): SSOFormShape {
  if (existingConnection) {
    return {
      displayName: existingConnection.ssoConnection.displayName,
      emailDomains: existingConnection.ssoConnection.boundEmailDomains.map(
        (d) => ({ value: d })
      ),
      enableJIT: !!existingConnection.jitEnabledAt,
    };
  }
  return {
    displayName: '',
    emailDomains: [{ value: '' }],
    enableJIT: true,
  };
}

export function SSOSetupSection({
  workosOrganizationId,
  existingSSOConnections,
}: SSOSetupSectionProps) {
  const { showFeedback, createErrorFeedback } = useFeedback();
  const [result, setResult] = useState<string | null>(null);

  // Filter out internal Luminary SSO connections and use the first customer connection
  const customerConnections = existingSSOConnections.filter(
    (conn) => !conn.ssoConnection.isLuminary
  );
  const existingConnection = customerConnections[0];
  const isEditMode = !!existingConnection;
  const existingConnectionDefaults = useMemo(
    () => getDefaultValues(existingConnection),
    [existingConnection]
  );

  const formMethods = useForm<SSOFormShape>({
    defaultValues: existingConnectionDefaults,
  });

  useEffect(() => {
    formMethods.reset(existingConnectionDefaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formMethods is intentionally excluded; react-hook-form creates a new object reference on every render, including it would cause an infinite loop
  }, [existingConnectionDefaults]);

  const [createSSOConnection, { loading: creatingSSO }] =
    useCreateSsoConnectionFromAdminMutation({
      onError: createErrorFeedback('Failed to create SSO connection'),
    });

  const [createTenantSSOConnection, { loading: attachingSSO }] =
    useCreateTenantSsoConnectionFromAdminMutation({
      onError: createErrorFeedback('Failed to attach SSO connection to tenant'),
      ...SSO_REFETCH_CONFIG,
    });

  const [updateSSOConnection, { loading: updatingSSO }] =
    useUpdateSsoConnectionFromAdminMutation({
      onError: createErrorFeedback('Failed to update SSO connection'),
      ...SSO_REFETCH_CONFIG,
    });

  const [updateTenantSSOConnection, { loading: updatingTenantSSO }] =
    useUpdateTenantSsoConnectionFromAdminMutation({
      onError: createErrorFeedback('Failed to update tenant SSO connection'),
      ...SSO_REFETCH_CONFIG,
    });

  const loading =
    creatingSSO || attachingSSO || updatingSSO || updatingTenantSSO;

  const handleUpdate = useCallback(
    async (formValues: SSOFormShape) => {
      if (!existingConnection) return;

      const domains = compact(
        formValues.emailDomains.map((d) => d.value.trim())
      );

      const updateSSOConnectionResult = await updateSSOConnection({
        variables: {
          input: {
            id: existingConnection.ssoConnection.id,
            update: {
              boundEmailDomains: domains,
            },
          },
        },
      });

      if (
        updateSSOConnectionResult.errors ||
        !updateSSOConnectionResult.data?.updateSSOConnection
      ) {
        return;
      }

      const shouldUpdateJITSetting =
        formValues.enableJIT !== Boolean(existingConnection.jitEnabledAt);

      if (shouldUpdateJITSetting) {
        const updateTenantSSOConnectionResult = await updateTenantSSOConnection(
          {
            variables: {
              input: {
                id: existingConnection.id,
                update: formValues.enableJIT
                  ? { jitEnabledAt: new Date() }
                  : { clearJitEnabledAt: true },
              },
            },
          }
        );

        if (
          updateTenantSSOConnectionResult.errors ||
          !updateTenantSSOConnectionResult.data?.updateTenantSSOConnection
        ) {
          return;
        }
      }

      showFeedback('SSO connection updated successfully', {
        variant: 'success',
      });
    },
    [
      existingConnection,
      updateSSOConnection,
      updateTenantSSOConnection,
      showFeedback,
    ]
  );

  const handleCreate = useCallback(
    async (formValues: SSOFormShape) => {
      if (!workosOrganizationId) return;

      const domains = compact(
        formValues.emailDomains.map((d) => d.value.trim())
      );

      const { data: ssoData } = await createSSOConnection({
        variables: {
          input: {
            create: {
              displayName: formValues.displayName,
              boundEmailDomains: domains,
              workosOrganizationID: workosOrganizationId,
            },
          },
        },
      });

      const ssoId = ssoData?.createSSOConnection?.id;
      if (!ssoId) return;

      // Note: these two mutations are not atomic. If createTenantSSOConnection fails,
      // an orphaned SSOConnection (id: ssoId) will exist in WorkOS. The onError callback
      // surfaces the ID so an admin can manually clean it up. A backend-level atomic
      // mutation would be needed to fully address this.
      const { data: tenantSSOData } = await createTenantSSOConnection({
        variables: {
          input: {
            create: {
              ssoConnectionID: ssoId,
              enableJIT: formValues.enableJIT,
            },
          },
        },
      });

      if (tenantSSOData?.createTenantSSOConnection) {
        showFeedback('SSO connection created and attached to tenant', {
          variant: 'success',
        });
        setResult(
          `SSO Connection ID: ${ssoId}\nTenant SSO Connection ID: ${tenantSSOData.createTenantSSOConnection.id}`
        );
      }
    },
    [
      workosOrganizationId,
      createSSOConnection,
      createTenantSSOConnection,
      showFeedback,
    ]
  );

  const onSubmit = formMethods.handleSubmit((formValues) => {
    if (isEditMode) {
      return handleUpdate(formValues);
    }
    return handleCreate(formValues);
  });

  if (!workosOrganizationId) {
    return (
      <Callout severity="warning">
        This tenant does not have a WorkOS Organization ID configured. SSO setup
        requires a WorkOS Organization ID.
      </Callout>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2} maxWidth={400}>
          <Typography variant="body-xs">
            WorkOS Organization ID: {workosOrganizationId}
          </Typography>
          {isEditMode && (
            <Typography variant="body-xs">
              SSO Connection ID: {existingConnection.ssoConnection.id}
            </Typography>
          )}
          <FormAwareTextInput<SSOFormShape>
            control={formMethods.control}
            fieldName="displayName"
            label="Display name"
            placeholder="e.g., Acme Corporation"
            required
            disabled={isEditMode}
            helpText={
              isEditMode
                ? 'Display name cannot be changed after creation'
                : undefined
            }
          />
          <Box>
            <Typography variant="title-xs">Email domains</Typography>
            <FormAwareInputRepeater<SSOFormShape>
              name="emailDomains"
              control={formMethods.control}
              emptyValue={{ value: '' }}
              addAnotherItemText="Add domain"
              isSingleLineRepeatedInput={false}
              render={(i) => (
                <FormAwareTextInput<SSOFormShape>
                  control={formMethods.control}
                  fieldName={`emailDomains.${i}.value` as const}
                  label=""
                  placeholder="e.g., pinionglobal.com"
                />
              )}
            />
          </Box>
          <FormAwareCheckbox
            control={formMethods.control}
            label="Enable JIT provisioning"
            fieldName="enableJIT"
          />
          <Box>
            <Button loading={loading} variant="primary" type="submit" size="sm">
              {isEditMode ? 'Update SSO' : 'Set up SSO'}
            </Button>
          </Box>
        </Stack>
        {result && (
          <Callout severity="success" sx={{ whiteSpace: 'pre-line' }}>
            {result}
          </Callout>
        )}
      </Box>
    </FormProvider>
  );
}
